"use client";

import React from "react";
import { Camera, MapPin, Compass, PenTool, Stamp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TheWandererBehindTheLens() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll();
  const {
    ref: collageRef,
    isVisible: collageVisible
  } = useRevealOnScroll();
  return <section data-section-id="15575" id="collective-discovery-since-childhood" className="bg-accent/50 text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-0 h-full w-full opacity-10">
        <div className="absolute top-10 left-10 h-64 w-64 rounded-full bg-secondary blur-3xl" />
        <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-primary blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={cn("mb-20 max-w-4xl transition-all duration-1000 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-secondary" />
            <span className="text-secondary font-mono tracking-widest uppercase text-sm">
              Collective Discovery
            </span>
          </div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.9] font-bold tracking-tight mb-8 text-secondary">
            Since Childhood: <br />
            <span className="italic font-light text-primary">
              Shared Journey
            </span>
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {}
          <div ref={contentRef} className={cn("lg:col-span-5 flex flex-col gap-10 transition-all duration-1000 delay-200 ease-out", contentVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10")}>
            <div className="prose prose-lg text-muted-foreground font-sans">
              <p className="first-letter:text-7xl first-letter:font-default first-letter:text-secondary first-letter:mr-3 first-letter:float-left leading-relaxed">
                Andrei Vargas and Mark Valencia, Ph.D., have traveled together since birth. 
                Mentored by parents who viewed the world as a classroom, they were taught a 
                simple yet profound ethos: "Always leave it better than you found it; 
                not just the destination, but the traveler."
              </p>
              <p className="mt-6 leading-relaxed">
                With 70 years of combined global travel experience, PS Travel Company was 
                forged in the spirit of humanitarian aid and refined by a decade of 
                boutique curation. We don't just offer destinations—we curate 
                transformative experiences.
              </p>
            </div>

            <div className="relative p-8 border-l-2 border-secondary/30 bg-secondary/5 backdrop-blur-sm my-4">
              <QuoteIcon className="absolute top-4 left-4 h-8 w-8 text-secondary/20" />
              <blockquote className="font-default text-2xl italic text-foreground relative z-10">
                "To travel is to save the world, one connection at a time. Do well, and 
                most importantly, do good."
              </blockquote>
              <cite className="block mt-4 font-mono text-sm font-bold tracking-wide uppercase not-italic text-secondary">
                — Andrei & Mark
              </cite>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="rounded-none px-8 bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="#">Our Story</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-none px-8 border-secondary/40 text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors">
                <Link to="#">Curation</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={collageRef} className={cn("lg:col-span-7 relative min-h-[600px] lg:min-h-[800px] w-full transition-all duration-1000 delay-400 ease-out", collageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20")}>
            {}
            <div className="absolute top-0 right-0 w-3/4 md:w-2/3 z-10 shadow-2xl rotate-2 border-8 border-white bg-white">
              <AspectRatio ratio={3 / 4}>
                <img src="https://certain-robin.10web.cloud/wp-content/uploads/2026/04/IMG_2231-scaled.jpg" alt="Founders Andrei and Mark" className="object-cover w-full h-full contrast-125" />
              </AspectRatio>
              <div className="absolute -bottom-12 -left-12 bg-card p-4 shadow-lg max-w-[200px] hidden md:block rotate-[-4deg]">
                <p className="font-default text-sm text-muted-foreground italic">Magdalena, Colombia, 2025.
The beginning of the journey.</p>
              </div>
            </div>

            {}
            

            {}
            <div className="absolute top-10 left-0 md:left-10 z-30 animate-pulse duration-[5000ms]">
              <div className="h-24 w-24 rounded-full border-4 border-secondary/40 flex items-center justify-center rotate-[-15deg] opacity-80 mix-blend-screen bg-background/50 backdrop-blur-[1px]">
                <div className="text-center">
                  <MapPin className="h-6 w-6 mx-auto text-secondary/60" />
                  <span className="block text-[10px] font-mono font-bold uppercase text-secondary/60 mt-1">
                    PS Travel
                  </span>
                  <span className="block text-[8px] font-mono uppercase text-secondary/60">Est. 2026</span>
                </div>
              </div>
            </div>

            

            {}
            <div className="absolute top-[-10px] right-[20%] w-32 h-8 bg-secondary/20 rotate-[-2deg] z-40 shadow-sm opacity-90" />
            
          </div>
        </div>

        {}
        <div className="mt-20 pt-10 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{
          icon: MapPin,
          label: "Years Experience",
          value: "70"
        }, {
          icon: Camera,
          label: "Destinations",
          value: "50+"
        }, {
          icon: Compass,
          label: "Impact Projects",
          value: "100+"
        }, {
          icon: PenTool,
          label: "Curated Trips",
          value: "1k+"
        }].map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center gap-2 group">
              <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center mb-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <stat.icon className="h-5 w-5" />
              </div>
              <span className="font-default text-3xl font-bold text-secondary">
                {stat.value}
              </span>
              <span className="text-sm text-muted-foreground uppercase tracking-widest font-mono">
                {stat.label}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}
function QuoteIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.0166 21L5.0166 18C5.0166 16.8954 5.91203 16 7.0166 16H10.0166C10.5689 16 11.0166 15.5523 11.0166 15V9C11.0166 8.44772 10.5689 8 10.0166 8H6.0166C5.46432 8 5.0166 8.44772 5.0166 9V11C5.0166 11.5523 4.56889 12 4.0166 12H3.0166V5H13.0166V15C13.0166 18.3137 10.3303 21 7.0166 21H5.0166Z" />
    </svg>;
}